import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import Star from '@mui/icons-material/Star'
import {
    Box,
    Chip,
    IconButton,
    Popover,
    Rating,
    Typography,
} from '@mui/material'
import { grey } from '@mui/material/colors'
import { GetCandidateProfileForRecruiterQuery } from '__generated__/graphql'
import { useState } from 'react'
import RatingsSummary from 'recruiter/pre-ralph/people/RatingsSummary'
import useIsMobile from 'shared/hooks/useIsMobile'

interface CandidateRatingDisplayProps {
    candidate: NonNullable<GetCandidateProfileForRecruiterQuery['candidate']>
    showNumber?: boolean
    showCount?: boolean
    showDetails?: boolean
}

const CandidateRatingDisplay = ({
    candidate,
    showNumber = false,
    showCount = false,
    showDetails = false,
}: CandidateRatingDisplayProps) => {
    const [detailsOpen, setDetailsOpen] = useState(false)
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
    const isMobile = useIsMobile()

    const averageRating =
        candidate.recruiterReviewSummary?.averageRating ?? null
    const totalCount =
        candidate.notes?.filter(
            (note) =>
                note.isActive &&
                !(
                    note.note.__typename === 'CandidateNote' &&
                    note.starRating === null
                ) &&
                !(
                    note.note.__typename === 'CandidateGroupNotes' &&
                    note.starRating === 0
                )
        ).length ?? 0
    const ratingCount = candidate.recruiterReviewSummary?.ratingCount ?? 0
    const stats =
        candidate.recruiterReviewSummary?.stats.map((stat) => ({
            averageRating: stat.averageRating ?? 0,
            count: stat.count ?? 0,
            noteType: stat.noteType,
        })) ?? []

    const handleToggleDetails = () => {
        setDetailsOpen(!detailsOpen)
    }

    const isEmpty = averageRating === null || totalCount === 0

    const hasRemeetNotes =
        candidate.notes?.some(
            (note) => note.isActive && note.excludeFromEvaluation
        ) ?? false

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box
                ref={(el: HTMLDivElement) => setAnchorEl(el)}
                sx={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    gap: 0.25,
                }}
            >
                {isEmpty && hasRemeetNotes ? (
                    <Chip
                        label="RM"
                        size="small"
                        sx={{
                            height: 20,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                        }}
                    />
                ) : (
                    <>
                        <Rating
                            name="candidate-stars"
                            value={isEmpty ? 5 : averageRating}
                            readOnly
                            precision={0.1}
                            size="small"
                            icon={
                                <Star
                                    fontSize="inherit"
                                    sx={{
                                        color: isEmpty ? grey[300] : undefined,
                                    }}
                                />
                            }
                            emptyIcon={
                                <Star
                                    fontSize="inherit"
                                    sx={{ color: grey[300] }}
                                />
                            }
                            sx={{ position: 'relative', bottom: 2 }}
                        />

                        {(showNumber || showCount) && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                (
                                {isEmpty ? (
                                    'no ratings'
                                ) : (
                                    <>
                                        {showNumber &&
                                            averageRating !== null &&
                                            averageRating.toFixed(1)}
                                        {showNumber &&
                                            averageRating !== null &&
                                            showCount &&
                                            totalCount > 0 &&
                                            ', '}
                                        {showCount &&
                                            totalCount > 0 &&
                                            `${totalCount} ${totalCount === 1 ? 'rating' : 'ratings'}`}
                                    </>
                                )}
                                )
                            </Typography>
                        )}

                        {!isEmpty && showDetails && (
                            <IconButton
                                size="small"
                                onClick={handleToggleDetails}
                                sx={{ p: 0, position: 'relative', bottom: 2 }}
                            >
                                {detailsOpen ? (
                                    <ArrowDropUpIcon fontSize="small" />
                                ) : (
                                    <ArrowDropDownIcon fontSize="small" />
                                )}
                            </IconButton>
                        )}
                    </>
                )}
            </Box>

            {/* Mobile: Direct render */}
            {showDetails && detailsOpen && isMobile && (
                <RatingsSummary
                    ratingCount={ratingCount}
                    totalCount={totalCount}
                    stats={stats}
                />
            )}

            {/* Desktop: Popover */}
            {showDetails && !isMobile && (
                <Popover
                    open={detailsOpen}
                    anchorEl={anchorEl}
                    onClose={() => setDetailsOpen(false)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    sx={{
                        border: '1px solid',
                        borderColor: 'other.lightBorder',
                    }}
                >
                    <RatingsSummary
                        ratingCount={ratingCount}
                        totalCount={totalCount}
                        stats={stats}
                    />
                </Popover>
            )}
        </Box>
    )
}

export default CandidateRatingDisplay
